"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";

import { COUNTRY_DIAL_CODES } from "./countryDialCodes";
import styles from "./purchase.module.css";

type Phase = "editing" | "creating" | "success";

type EnterpriseFormState = {
	companyName?: string;
	firstName: string;
	lastName: string;
	email?: string;
	countryCode: string;
	phoneNumber: string;
	salesRepresentativePhoneNumber: string;
	password: string;
	confirmPassword: string;
};

const INITIAL_FORM: EnterpriseFormState = {
	companyName: "",
	firstName: "",
	lastName: "",
	email: "",
	countryCode: "+243",
	phoneNumber: "",
	salesRepresentativePhoneNumber: "",
	password: "",
	confirmPassword: "",
};

const escapeForRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeLocalPhoneNumber = (rawValue: string, countryCode: string) => {
	let cleaned = rawValue.replace(/[^\d+]/g, "").trim();
	if (!cleaned) return "";

	if (cleaned.startsWith("+")) {
		cleaned = cleaned.slice(1);
	}

	const dialDigits = countryCode.replace(/\D/g, "");
	const countryPattern = new RegExp(`^(?:00)?${escapeForRegex(dialDigits)}`);
	cleaned = cleaned.replace(countryPattern, "");
	cleaned = cleaned.replace(/^0+/, "");

	return cleaned;
};

const toInternationalPhoneNumber = (countryCode: string, rawValue: string) => {
	const normalizedLocal = normalizeLocalPhoneNumber(rawValue, countryCode);
	return normalizedLocal ? `${countryCode}${normalizedLocal}` : "";
};

export default function PurchasePage() {
	const searchParams = useSearchParams();
	const [form, setForm] = useState<EnterpriseFormState>(INITIAL_FORM);
	const [phase, setPhase] = useState<Phase>("editing");
	const [statusMessage, setStatusMessage] = useState("");
	const [statusTone, setStatusTone] = useState<"neutral" | "error" | "success">("neutral");
	const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL?.trim();
	const apiRoot = apiBaseUrl ? `${apiBaseUrl.replace(/\/+$/, "")}/pharmacy` : "";
	const isProduction = process.env.NODE_ENV === "production";
	const isApiBlocked = isProduction && !apiBaseUrl;
	const enterprisePhoneNumber = toInternationalPhoneNumber(form.countryCode, form.phoneNumber);
	const isEmbedded = searchParams.get("embedded") === "1";
	const [fakeFieldValue, setFakeFieldValue] = useState("");


	const setField = (field: keyof EnterpriseFormState, value: string) => {
		setForm((current) => ({
			...current,
			[field]: value,
		}));
	};

	const handlePhoneNumberChange = (value: string) => {
		setForm((current) => ({
			...current,
			phoneNumber: value.replace(/[^\d\s().+-]/g, ""),
		}));
	};

	const handleCountryCodeChange = (value: string) => {
		setForm((current) => ({
			...current,
			countryCode: value,
			phoneNumber: normalizeLocalPhoneNumber(current.phoneNumber, value),
		}));
	};

	const handleSubmit = async (event: React.SubmitEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (isApiBlocked) {
			setStatusTone("error");
			setStatusMessage("Configuration API manquante. Création bloquée en production.");
			return;
		}

		const normalizedPhone = toInternationalPhoneNumber(form.countryCode, form.phoneNumber);

		// if (!normalizedPhone.startsWith("+")) {
		// 	setStatusTone("error");
		// 	setStatusMessage("Numéro de téléphone invalide.");
		// 	return;
		// }

		if (form.password.trim().length < 6) {
			setStatusTone("error");
			setStatusMessage("Le mot de passe doit contenir au moins 6 caractères.");
			return;
		}

		if (form.password !== form.confirmPassword) {
			setStatusTone("error");
			setStatusMessage("Les mots de passe ne correspondent pas.");
			return;
		}

		if (!apiBaseUrl) {
			setPhase("success");
			setStatusTone("success");
			setStatusMessage("Formulaire validé. Aucun envoi API effectué dans cet environnement de développement.");
			return;
		}

		setPhase("creating");
		setStatusTone("neutral");
		setStatusMessage("");

		try {
			const response = await fetch(`${apiRoot}/createEnterprise`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
				body: JSON.stringify({
					// companyName: form.companyName?.trim(),
					firstName: form.firstName.trim(),
					lastName: form.lastName.trim(),
					email: form.email?.trim() || null,
					phoneNumber: normalizedPhone,
					// salesRepresentativePhoneNumber: form.salesRepresentativePhoneNumber?.trim() || null,
					password: form.password,
				}),
			});

			if (!response.ok) {
				const payload = await response.json().catch(() => null);
				const reason = payload && typeof payload === "object" && "reason" in payload ? payload.reason : null;
				const duplicateFields = payload && typeof payload === "object" && "duplicateFields" in payload && Array.isArray(payload.duplicateFields)
					? payload.duplicateFields as string[]
					: [];

				if (reason === "duplicate_enterprise_admin_contact") {
					const duplicateLabel = duplicateFields.length > 0 ? duplicateFields.join(" / ") : "phone or email";
					throw new Error(`duplicate:${duplicateLabel}`);
				}

				throw new Error("create-enterprise-failed");
			}

			setPhase("success");
			setStatusTone("success");
			setStatusMessage("Votre compte a été créée avec succès. veillez télécharger l'application en cliquant sur le bouton ci-dessous");
		} catch (error) {
			setPhase("editing");
			setStatusTone("error");
			if (error instanceof Error && error.message.startsWith("duplicate:")) {
				setStatusMessage("Votre compte existe déjà; veillez créer plutôt une pharmacie dans votre app");
				return;
			}

			setStatusMessage("Impossible de créer l'entreprise pour le moment.");
		}
	};

	const isBusy = phase === "creating";

	useEffect(() => {
		if (!isEmbedded || phase !== "success" || typeof window === "undefined") return;

		window.parent?.postMessage(
			{
				type: "pharmapp-purchase-success",
				phoneNumber: enterprisePhoneNumber,
			},
			"*",
		);
	}, [enterprisePhoneNumber, isEmbedded, phase]);

	return (
		<>
			{isEmbedded ? null : <SiteHeader />}
			<div className={`${styles.page} ${isEmbedded ? styles.pageEmbedded : ""}`}>
			<div className={`${styles.shell} ${isEmbedded ? styles.shellEmbedded : ""}`}>
				<section className={`${styles.section} ${isEmbedded ? styles.sectionEmbedded : ""}`}>
					<motion.div
						className={`${styles.card} ${isEmbedded ? styles.cardEmbedded : ""}`}
						initial={{ opacity: 0, y: 18 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.32, ease: "easeOut" }}
					>
						{phase !== "success" ? (
							<form className={styles.form} onSubmit={handleSubmit}>
								<h1 className={styles.title}>Créer votre compte</h1>

								<div className={styles.formGrid}>
									{/* <div className={styles.fieldFull}>
										<label className={styles.label} htmlFor="companyName">
											Nom entreprise
										</label>
										<input
											id="companyName"
											className={styles.input}
											value={form.companyName}
											onChange={(event) => setField("companyName", event.target.value)}
											placeholder="Pharmacie Sainte Marie"
											required
										/>
									</div> */}

									<div className={styles.field}>
										<label className={styles.label} htmlFor="firstName">
											Prénom
										</label>
										<input
											id="firstName"
											className={styles.input}
											value={form.firstName}
											onChange={(event) => setField("firstName", event.target.value)}
											placeholder="Grâce"
											required
										/>
									</div>

									<div className={styles.field}>
										<label className={styles.label} htmlFor="lastName">
											Nom
										</label>
										<input
											id="lastName"
											className={styles.input}
											value={form.lastName}
											onChange={(event) => setField("lastName", event.target.value)}
											placeholder="Mukendi"
											required
										/>
									</div>

									<div className={styles.fieldFull}>
										<label className={styles.label} htmlFor="email">
											E-mail
										</label>
										<input
											id="email"
											className={styles.input}
											type="email"
											value={form.email}
											onChange={(event) => setField("email", event.target.value)}
											placeholder="gracemukendi@gmail.com"
										/>
									</div>

									{/* <div className={styles.field}>
										<label className={styles.label} htmlFor="salesRepresentativePhoneNumber">
											Téléphone responsable de vente (optionnel)
										</label>
										<input
											id="salesRepresentativePhoneNumber"
											className={styles.input}
											type="tel"
											value={form.salesRepresentativePhoneNumber}
											onChange={(event) => setField("salesRepresentativePhoneNumber", event.target.value.replace(/[^\d\s().+-]/g, ""))}
											placeholder="+243970000000"
										/>
									</div> */}

									<div className={styles.fieldFull}>
										<label className={styles.label} htmlFor="phoneNumber">
											Téléphone
										</label>
										<div className={styles.phoneRow}>
											<div className={styles.selectWrap}>
												<select
													className={styles.countrySelect}
													value={form.countryCode}
													onChange={(event) => handleCountryCodeChange(event.target.value)}
													aria-label="Indicatif pays"
												>
													{COUNTRY_DIAL_CODES.map((option) => (
														<option key={`${option.isoCode}-${option.code}`} value={option.code}>
															{option.code} {option.label}
														</option>
													))}
												</select>
												<span className={styles.selectIcon} aria-hidden="true">
													<i className="bi bi-chevron-down"></i>
												</span>
											</div>
											<input
												id="phoneNumber"
												className={styles.input}
												type="tel"
												value={form.phoneNumber}
												onChange={(event) => handlePhoneNumberChange(event.target.value)}
												placeholder="970000000"
												required
											/>
										</div>
									</div>

									<div className={styles.field}>
										<label className={styles.label} htmlFor="password">
											Mot de passe
										</label>
										<input
											id="password"
											className={styles.input}
											type="password"
											value={form.password}
											onChange={(event) => setField("password", event.target.value)}
											placeholder="••••••••"
											required
										/>
									</div>

									<div className={styles.field}>
										<label className={styles.label} htmlFor="confirmPassword">
											Confirmer mot de passe
										</label>
										<input
											id="confirmPassword"
											className={styles.input}
											type="password"
											value={form.confirmPassword}
											onChange={(event) => setField("confirmPassword", event.target.value)}
											placeholder="••••••••"
											required
										/>
									</div>

									{/* fake field for detecting robots */}
									<input type="text" name="adresse" style={{position: 'absolute', left: '-9999px', opacity: 0,pointerEvents: 'none',}} value={fakeFieldValue} onChange={(e)=> setFakeFieldValue(e.target.value) }/>

								</div>

								<div className={styles.actions}>
									<button type="submit" className={styles.primaryButton} disabled={ phase === "creating" || isBusy || isApiBlocked}>
										{phase === "creating" ? "Création en cours..." : "Créer le compte"}
									</button>
									<Link href="/nous-contacter" className={styles.helpButton}>
										Besoin d&apos;aide
									</Link>
								</div>

								{isApiBlocked ? <p className={styles.statusError}>Configuration API manquante. Création bloquée en production.</p> : null}

								{statusMessage ? (
									<p
										className={
											statusTone === "error"
												? styles.statusError
												: statusTone === "success"
													? styles.statusSuccess
													: styles.status
										}
									>
										{statusMessage}
									</p>
								) : null}
							</form>
						) : null}

						{phase === "success" ? (
							<div className={styles.form}>
								<h1 className={styles.title}>Compte crée</h1>
								{statusMessage ? <p className={styles.statusSuccess}>{statusMessage}</p> : null}
								<div className={styles.successBody}>
									<p className={styles.successParagraph}>
										{isEmbedded
											? "Vous pouvez maintenant fermer cette fenêtre et vous connecter dans l'application."
											: null}
									</p>
									<p className={styles.successParagraph}>
										Utilisez le numéro de téléphone et le mot de passe fournis ici.
										{enterprisePhoneNumber ? ` Numéro utilisé: ${enterprisePhoneNumber}.` : ""}
									</p>
								</div>
								{isEmbedded ? null : (
									<div className={styles.actions}>
										<Link href="/download" className={styles.primaryButton}>
											Télécharger l&apos;application
										</Link>
									</div>
								)}
							</div>
						) : null}

						{isEmbedded ? null : (
							<div className={styles.homeLinkRow}>
								<Link href="/" className={styles.homeLink}>
									Retour à l&apos;accueil
								</Link>
							</div>
						)}
					</motion.div>
				</section>
			</div>
			</div>
			{isEmbedded ? null : <SiteFooter />}
		</>
	);
}
